import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { deleteServiceOccurrence } from "@/api/services";
import { operationError } from "@/api/ministryOperations";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/design-system/dialogs/ConfirmDialog";
import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";
import type { ServiceOccurrence } from "@/types/service";

export function OccurrenceDeletion({ churchId, service }: { churchId: string; service: ServiceOccurrence }) {
  const { permissions } = useEffectivePermissions(service.ministry_id);
  const [confirming, setConfirming] = useState(false);
  const deleting = useRef(false);
  const client = useQueryClient();
  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: () => deleteServiceOccurrence(churchId, service.id),
    onSuccess: () => {
      setConfirming(false);
      navigate(service.ministry_id ? `/services?ministry_id=${service.ministry_id}` : "/services", { replace: true });
      client.removeQueries({ queryKey: ["service-occurrence", churchId, service.id], exact: true });
      void client.invalidateQueries({ predicate: query => query.queryKey[1] === churchId });
    },
    onError: () => setConfirming(false),
    onSettled: () => { deleting.current = false; },
  });
  if (!permissions.has("SERVICES_MANAGE") || (!service.ministry_id && !permissions.has("SETTINGS_MANAGE"))) return null;
  return <section aria-label="Occurrence deletion" className="mt-6 space-y-3 rounded-2xl border border-rose-500/20 p-5">
    <h2 className="font-semibold">Delete this occurrence</h2>
    <p className="text-sm text-slate-400">{service.schedule_id ? "Remove only this dated occurrence. The recurring service definition and other occurrences remain. A removed recurring date will not be generated again." : "Remove this one-time service."} Attendance, visitor or audit history prevents deletion.</p>
    <Button variant="danger" disabled={mutation.isPending} onClick={() => { mutation.reset(); setConfirming(true); }}>Delete occurrence</Button>
    {mutation.isError && <p role="alert" className="text-rose-300">{operationError(mutation.error)}</p>}
    <ConfirmDialog open={confirming} title="Delete this occurrence?"
      description={`Delete ${service.title} on ${new Date(service.starts_at).toLocaleString()}? This cannot be undone. ${service.schedule_id ? "Its recurring schedule will remain, and this recurring date will stay removed. " : ""} The server will check protected history before deleting.`}
      destructive confirmText="Delete occurrence" pending={mutation.isPending}
      onCancel={() => { if (!mutation.isPending) setConfirming(false); }}
      onConfirm={() => { if (!deleting.current) { deleting.current = true; mutation.mutate(); } }} />
  </section>;
}
